import React, {
  createContext,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  CART_COUNTER_TIME_LIMIT,
  CART_LOCAL_STORAGE_KEY,
  Flow,
} from '../services/viva/constants';
import {
  actions,
  selectCheckoutCartAddOnsSelectionsCount,
  selectCheckoutItemsFlat,
  selectFirstTimeSlot,
  selectFlow,
  selectShopCartSelections,
  selectTotalQuantity,
  useAppDispatch,
  useAppSelector,
} from '../store';
import {
  getObjectForLocalStorageKey,
  removeObjectForLocalStorageKey,
} from '../store/utils';

import {
  useGetEditQueryParams,
  getIsBuyTicketPage,
} from './use-ticket-edit-mode';

import type { CheckoutType, ShopCartSelection } from '../store/types';

type CountdownTimerContextType = {
  timeRemaining: number | null;
  isSessionExpired?: boolean;
  startCounter: () => void;
  clearCounter: () => void;
};

type Props = {
  children: React.ReactNode;
};

export const CountdownTimerContext = createContext<CountdownTimerContextType>({
  timeRemaining: null,
  isSessionExpired: false,
  startCounter: () => {
    /* noop */
  },
  clearCounter: () => {
    /* noop */
  },
});

export const CountdownTimerProvider = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const { editMode } = useGetEditQueryParams();
  const wizard = useAppSelector((state) => state.wizard);
  const selectedTimeSlot = useAppSelector(selectFirstTimeSlot);
  const shopCartSelections = useAppSelector(selectShopCartSelections);
  const checkoutCartAddOnsSelectionsCount = useAppSelector(
    selectCheckoutCartAddOnsSelectionsCount,
  );
  const totalSelections = useAppSelector(selectTotalQuantity);
  const checkoutItems = useAppSelector(selectCheckoutItemsFlat);
  const flow = useAppSelector(selectFlow);

  const [counterEndTime, setCounterEndTime] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  const startCounter = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + CART_COUNTER_TIME_LIMIT);

    dispatch(actions.wizard.setCounterEndTime(now.getTime()));
  };

  const clearCounter = useCallback(() => {
    if (
      (!checkoutItems || checkoutItems.length === 0) &&
      checkoutCartAddOnsSelectionsCount === 0
    ) {
      dispatch(actions.wizard.clearCounter());
      dispatch(actions.order.resetShopCart());
      setCounterEndTime(null);
    }
  }, [dispatch, checkoutItems]);

  // On mount, initialize checkout
  useEffect(() => {
    dispatch(actions.checkout.initialize());
  }, [dispatch]);

  // Clear counter when cart is emptied and not on buy ticket pages
  // allows closing the cart modal without losing the cart
  useEffect(() => {
    const isBuyTicketPage = getIsBuyTicketPage();

    if (!isBuyTicketPage) {
      clearCounter();
    }
  }, [clearCounter, checkoutItems]);

  // Set counter end time when a date is selected
  useEffect(() => {
    // If we're in edit mode, do not update the counter end time
    if (editMode && counterEndTime) return;

    // Do not set timer if no selections
    if (totalSelections === 0) return;

    if (
      (flow === Flow.TOR_EXPRESS ||
        (selectedTimeSlot?.startRaw.hours ?? 0) > 0) &&
      !wizard.counterEndTime
    ) {
      startCounter();
    }
  }, [selectedTimeSlot, dispatch]);

  // Watch for changes in wizard counter end time, update local state end time
  useEffect(() => {
    if (wizard.counterEndTime) {
      setCounterEndTime(wizard.counterEndTime);
    }
  }, [wizard.counterEndTime]);

  // Set an interval when the values change
  useEffect(() => {
    if (!counterEndTime) {
      intervalRef.current = null;
      return;
    }

    intervalRef.current = setInterval(() => {
      if (!counterEndTime) {
        intervalRef.current = null;
        return;
      }

      const now = new Date().getTime();
      const timeDifferenceInMilliseconds = counterEndTime - now;
      const timeDifferenceInSeconds = Math.floor(
        timeDifferenceInMilliseconds / 1000,
      );

      const lsCart = getObjectForLocalStorageKey<CheckoutType>(
        CART_LOCAL_STORAGE_KEY,
      );

      let cartHasSelections = false;
      let selections: { [key in Flow]?: ShopCartSelection[] } = {
        [Flow.NONE]: [] as ShopCartSelection[],
      };
      if (shopCartSelections && shopCartSelections.length > 0) {
        selections = {
          [flow]: shopCartSelections,
        };
      } else if (lsCart?.cart?.selections) {
        selections = lsCart?.cart?.selections;
      }

      cartHasSelections = selections
        ? Object.keys(selections).reduce((acc, curr) => {
            const forFlow = selections[curr as Flow];

            return (
              acc +
              (forFlow?.reduce((qty, sel) => {
                return qty + sel.count;
              }, 0) ?? 0)
            );
          }, 0) > 0
        : false;

      // If count down is less than or equal to 0 OR if count down is greater than 20 minutes
      if (
        (timeDifferenceInSeconds <= 0 || timeDifferenceInSeconds / 60 > 20) &&
        cartHasSelections
      ) {
        // Timer up!
        setIsSessionExpired(true);
        removeObjectForLocalStorageKey(CART_LOCAL_STORAGE_KEY);
        dispatch(actions.checkout.reset());
        dispatch(actions.wizard.reset());
        dispatch(actions.order.reset());
        if (intervalRef.current)
          clearInterval(intervalRef.current as unknown as number);
      } else {
        setTimeRemaining(timeDifferenceInSeconds);
      }
    }, 1000);

    return () => {
      if (intervalRef.current)
        clearInterval(intervalRef.current as unknown as number);
    };
  }, [counterEndTime]);

  if (flow === Flow.CITY_PASS_REDEMPTION)
    return <Fragment>{children}</Fragment>;

  return (
    <CountdownTimerContext.Provider
      value={{ timeRemaining, isSessionExpired, startCounter, clearCounter }}
    >
      {children}
    </CountdownTimerContext.Provider>
  );
};
